import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  styled,
  TableCell,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  Autocomplete,
  keyframes,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import StyledSelectInput from "../../Common/StyledSelectInput";
import { getTimeSlots } from "../../../api/Customer/enquiry";
import { updateTrip } from "../../../api/Admin/trips";
import useToast from "../../../hooks/useToast";
import {
  getAllTransporters,
  getTrucksOfTransporterById,
} from "../../../api/Admin/transporterResources";
import { fetchAllTransporters } from "../../../api/Admin/vehicleTracking";
const colorSwitch = keyframes`
  0% { color: #ff5722; }   /* First color */
  50% { color:rgb(127, 159, 186); }  /* Second color */
  100% { color: #ff5722; } /* Back to first */
`;

const EditTrip = ({
  enquiryDetails,
  customerAmounts,
  transporterAmounts,
  tripId,
  getTripById,
  handleCloseModal,
  editTripProps,
}) => {
  // console.log(editTripProps);
  const [timeSlots, setTimeSlots] = useState([]);
  const { handleOpenToast, ToastMessage } = useToast();

  const { pickup, distance, material, quantity, weight } = enquiryDetails || {};

  const { LEMargin, finalAmount } = customerAmounts || {};
  const { finalBidAmount: transporterBidAmount } = transporterAmounts || {};

  const transporterRate = transporterBidAmount / (weight / 1000);

  const customerRate = finalAmount / (weight / 1000);

  const originalWeight = weight;
  const originalTransporterRate = Math.ceil(transporterRate);
  const originalCustomerRate = Math.ceil(customerRate);
  const originalPickupDate = moment(pickup?.date).format("YYYY-MM-DD");
  const originalPickupTime = pickup?.slot || "";
  const originalTransporter = editTripProps.transporterName;
  const originalTruck = editTripProps.vehicleNumber;

  const [editedWeight, setEditedWeight] = useState(weight);
  const [editedTransporterRate, setEditedTransporterRate] = useState(
    Math.ceil(transporterRate)
  );
  const [editedCustomerRate, setEditedCustomerRate] = useState(
    Math.ceil(customerRate)
  );
  const [editedTransporterBidAmount, setEditedTransporterBidAmount] = useState(
    Math.ceil(transporterBidAmount)
  );
  const [editedLEMargin, setEditedLEMargin] = useState(Math.ceil(LEMargin));
  const [editedTotalAmount, setEditedTotalAmount] = useState(
    Math.ceil(finalAmount)
  );
  const [editedPickupDate, setEditedPickupDate] = useState(
    moment(pickup?.date).format("YYYY-MM-DD")
  );
  const [editedPickupTime, setEditedPickupTime] = useState(pickup?.slot || "");

  const [isListFetched, setIsListFetched] = useState(false);
  const [isVehicleListFetched, setIsVehicleListFetched] = useState(false);
  const [transporteOptions, setTransporterOptions] = useState([]);
  const [transporter, setSelectedTransporter] = useState(
    editTripProps.transporterName
  );
  const [truck, setSelectedTruck] = useState(editTripProps.vehicleNumber);
  const [trucksOptions, setTruckOptions] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState({
    driverName: editTripProps.driverName,
    driverId: editTripProps.driverId,
    vehicleNumber: editTripProps.vehicleNumber,
    transporterVehicleId: editTripProps.transporterVehicleId,
  });
  const [isAllDetailsLoaded, setIsAllDetailsLoaded] = useState(false);

  const [isDriverAssignedError, setIsDriverAssignedError] = useState(false);
  const [isTransporterChanged, setIsTransporterChanged] = useState(false);

  const handleEditWeight = (e) => {
    const newWeight = e.target.value;
    const updatedTransporterBidAmount = Math.ceil(
      (newWeight / 1000) * editedTransporterRate
    );
    const updatedTotalAmount = Math.ceil(
      (newWeight / 1000) * editedCustomerRate
    );
    const updatedLEMargin = updatedTotalAmount - updatedTransporterBidAmount;

    setEditedWeight(newWeight);
    setEditedTransporterBidAmount(updatedTransporterBidAmount);
    setEditedTotalAmount(updatedTotalAmount);
    setEditedLEMargin(updatedLEMargin);
  };

  const handleEditTransporterRate = (e) => {
    const newTransporterRate = e.target.value;
    const updatedTransporterBidAmount = Math.ceil(
      (editedWeight / 1000) * newTransporterRate
    );
    const updatedLEMargin = editedTotalAmount - updatedTransporterBidAmount;

    setEditedTransporterRate(newTransporterRate);
    setEditedTransporterBidAmount(updatedTransporterBidAmount);
    setEditedLEMargin(updatedLEMargin);
  };

  const handleEditCustomerRate = (e) => {
    const newCustomerRate = e.target.value;
    const updatedTotalAmount = Math.ceil(
      (editedWeight / 1000) * newCustomerRate
    );
    const updatedLEMargin = updatedTotalAmount - editedTransporterBidAmount;

    setEditedCustomerRate(newCustomerRate);
    setEditedTotalAmount(updatedTotalAmount);
    setEditedLEMargin(updatedLEMargin);
  };

  const getSlots = async () => {
    try {
      const res = await getTimeSlots();
      const timeSlotForDropdown = res.data.data.map((slot, idx) => ({
        id: idx,
        value: slot.timeSlot,
        startTime: slot.startTime,
      }));
      setTimeSlots(timeSlotForDropdown);
      setIsAllDetailsLoaded(true);
    } catch (error) {
      handleOpenToast("Failed to get time slots", "error");
    }
  };
  const fetchVehiclesForTransporter = async (id) => {
    try {
      setIsVehicleListFetched(true);
      const response = await getTrucksOfTransporterById(id);
      console.log(response.data.data.vehicles);
      setTruckOptions(
        response.data.data.vehicles.map((v) => ({
          driverName: v.driverName,
          driverId: v.assignedTo,
          vehicleNumber: v.vehicleNumber,
          transporterVehicleId: v.transporterVehicleId,
        }))
      );
      setIsVehicleListFetched(false);
    } catch (error) {
    } finally {
      setIsVehicleListFetched(false);
    }
  };

  const handleOnSelectTransporter = (event, selectedOption) => {
    setIsTransporterChanged(true);
    const res = transporteOptions.find((t) => t.name === selectedOption);
    // console.log(res);
    // make a driver list call with id
    if (res) fetchVehiclesForTransporter(res.id);
  };

  const disableSaveButton = () => {
    const hasEmptyFields =
      !editedWeight ||
      !editedTransporterRate ||
      !editedCustomerRate ||
      !editedPickupDate ||
      !editedPickupTime ||
      !transporter ||
      !truck;

    const hasChanges = !(
      Number(editedWeight) === originalWeight &&
      Number(editedTransporterRate) === originalTransporterRate &&
      Number(editedCustomerRate) === originalCustomerRate &&
      editedPickupDate === originalPickupDate &&
      editedPickupTime === originalPickupTime &&
      transporter === originalTransporter &&
      truck === originalTruck
    );

    return hasEmptyFields || !hasChanges;
  };

  const handleEditTrip = async () => {
    // console.log(selectedVehicle);
    const data = {};
    setIsDriverAssignedError(false);
    if (!selectedVehicle?.driverId) {
      setIsDriverAssignedError(true);
      return;
    }
    if (editedWeight != null) data.weight = editedWeight;
    if (quantity != null) data.quantity = quantity;
    if (editedTotalAmount != null) data.customerFinalAmount = editedTotalAmount;
    if (editedTransporterBidAmount != null)
      data.transporterFinalAmount = editedTransporterBidAmount;
    if (editedLEMargin != null) data.LEMargin = editedLEMargin;
    if (editedPickupDate != null) data.tripDate = editedPickupDate;
    if (editedPickupTime != null) data.tripTimeSlot = editedPickupTime;
    // console.log({
    //   ...data,
    //   transporterId: transporteOptions.find((t) => t.name === transporter).id,
    //   transporterVehicleId:
    //     selectedVehicle?.transporterVehicleId ??
    //     editTripProps.transporterVehicleId,
    //   // editTripProps.transporterVehicleId,
    //   driverId: selectedVehicle?.driverId ?? editTripProps.driverId,
    // });
    // return;
    try {
      await updateTrip(tripId, {
        ...data,
        transporterId: transporteOptions.find((t) => t.name === transporter).id,
        transporterVehicleId:
          selectedVehicle?.transporterVehicleId ??
          editTripProps.transporterVehicleId,
        // editTripProps.transporterVehicleId,
        driverId: selectedVehicle?.driverId ?? editTripProps.driverId,
      });
      await getTripById();
      handleCloseModal();
      handleOpenToast("Trip Updated Successfully", "success");
    } catch (error) {
      handleOpenToast("Failed to update trip", "error");
    }
  };

  useEffect(() => {
    getSlots();
    if (editTripProps.transporterId)
      fetchVehiclesForTransporter(editTripProps.transporterId);
  }, []);

  useEffect(() => {
    if (transporter.trim() === "") {
      setTransporterOptions([]);
      return;
    }

    const fetchOptions = async () => {
      setIsListFetched(true);
      try {
        const response = await fetchAllTransporters(transporter);
        setTransporterOptions(
          response.data.data.transporters.map((e) => ({
            id: e.transporterId,
            name: e.transporterName,
          })) || []
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        setTransporterOptions([]);
      } finally {
        setIsListFetched(false);
      }
    };

    const debounceFetch = setTimeout(fetchOptions, 300); // Debounce API calls

    return () => clearTimeout(debounceFetch); // Cleanup timeout
  }, [transporter]);

  if (!isAllDetailsLoaded) return null;

  return (
    <Dialog open fullWidth maxWidth="lg">
      <ToastMessage />
      <DialogContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            Current Trip Details
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>
        <Box
          display="flex"
          flexDirection={"column"}
          justifyContent="flex-start"
          m={2}
        >
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <DescriptionBox>
              <HeadingTableCell>Material Type</HeadingTableCell>
              <DescriptionTableCell>
                {material?.name.length > 20
                  ? `${material?.name.substring(0, 20)}...`
                  : material?.name}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Weight</HeadingTableCell>
              <DescriptionTableCell>
                {weight ? `${weight} KG` : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>No Of Pkgs</HeadingTableCell>
              <DescriptionTableCell>{quantity || "-"}</DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Distance</HeadingTableCell>
              <DescriptionTableCell>
                {distance ? `${distance} KM` : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Transporter Rate</HeadingTableCell>
              <DescriptionTableCell>
                ₹ {transporterRate ? Math.ceil(transporterRate) : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Customer Rate</HeadingTableCell>
              <DescriptionTableCell>
                ₹ {customerRate ? Math.ceil(customerRate) : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Transporter Quote</HeadingTableCell>
              <DescriptionTableCell>
                ₹ {transporterBidAmount ? transporterBidAmount : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>LE Margine</HeadingTableCell>
              <DescriptionTableCell>
                ₹ {LEMargin ? LEMargin : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Final Amount</HeadingTableCell>
              <DescriptionTableCell>
                ₹ {finalAmount ? finalAmount : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
          </Box>
          <Box display="flex" justifyContent="flex-start" mt={4}>
            <DescriptionBox>
              <HeadingTableCell>Pick Up Date</HeadingTableCell>
              <DescriptionTableCell>
                {moment(pickup?.date).format("DD-MM-YYYY")}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Pick Up Time</HeadingTableCell>
              <DescriptionTableCell>{pickup?.slot || "-"}</DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Transporter Name</HeadingTableCell>
              <DescriptionTableCell>
                {editTripProps.transporterName || "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Vehicle</HeadingTableCell>
              <DescriptionTableCell>
                {editTripProps.vehicleNumber || "-"}
              </DescriptionTableCell>
            </DescriptionBox>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Edit And Update Trip Details
          </Typography>
          {isTransporterChanged && (
            <Typography
              variant="p1"
              sx={{
                textAlign: "center",
                animation: `${colorSwitch} 5s infinite`,
                fontSize: "14px",
              }}
            >
              (Make sure the vehicle you’ve selected isn’t already assigned to
              another trip during this trip’s service period.)
            </Typography>
          )}
        </Box>
        <Box
          display="flex"
          flexDirection={"column"}
          justifyContent="flex-start"
          m={2}
        >
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <DescriptionBox>
              <HeadingTableCell>Material Type</HeadingTableCell>
              <DescriptionTableCell>
                {material?.name.length > 20
                  ? `${material?.name.substring(0, 20)}...`
                  : material?.name}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Weight</HeadingTableCell>
              <InputField
                value={editedWeight}
                variant="outlined"
                size="small"
                onChange={handleEditWeight}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">KG</InputAdornment>
                  ),
                }}
              />
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>No Of Pkgs</HeadingTableCell>
              <DescriptionTableCell>
                {quantity ? quantity : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Distance</HeadingTableCell>
              <DescriptionTableCell>
                {distance ? `${distance} KM` : "-"}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Transporter Rate</HeadingTableCell>
              <InputField
                placeholder="5000"
                value={editedTransporterRate}
                onChange={handleEditTransporterRate}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹/Tone</InputAdornment>
                  ),
                }}
              />
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Customer Rate</HeadingTableCell>
              <InputField
                placeholder="5115"
                variant="outlined"
                value={editedCustomerRate}
                onChange={handleEditCustomerRate}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹/Tone</InputAdornment>
                  ),
                }}
              />
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Transporter Quote</HeadingTableCell>
              <DescriptionTableCell>
                ₹{editedTransporterBidAmount ? editedTransporterBidAmount : 0}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>LE Margine</HeadingTableCell>
              <DescriptionTableCell>
                ₹{editedLEMargin ? editedLEMargin : 0}
              </DescriptionTableCell>
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Final Amount</HeadingTableCell>
              <DescriptionTableCell>
                ₹{editedTotalAmount ? editedTotalAmount : 0}
              </DescriptionTableCell>
            </DescriptionBox>
          </Box>
          <Box display="flex" gap={4} justifyContent="flex-start" mt={4}>
            <DescriptionBox>
              <HeadingTableCell>Pick Up Date</HeadingTableCell>
              <InputField
                type="date"
                value={editedPickupDate}
                onChange={(e) => setEditedPickupDate(e.target.value)}
                variant="outlined"
                size="small"
              />
            </DescriptionBox>
            <DescriptionBox>
              <HeadingTableCell>Pick Up Time</HeadingTableCell>
              <StyledSelectInput
                name="slot"
                onChange={(e) => setEditedPickupTime(e.target.value)}
                items={timeSlots}
                value={editedPickupTime}
              />
            </DescriptionBox>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <HeadingTableCell>Transporter</HeadingTableCell>
              <Autocomplete
                sx={{
                  backgroundColor: "#f0f0f0",
                  width: 240,
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                    padding: "10px 8px 7.5px 15px", // same as your CSS but stable
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      height: "3rem",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2e7d32", // Hover effect
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1b5e20", // Focus effect
                    },
                  },
                }}
                options={transporteOptions.map((t) => t.name)}
                freeSolo
                value={transporter}
                onInputChange={(event, newInputValue) => {
                  setTruckOptions([]);
                  setSelectedTruck("");
                  setSelectedTransporter(newInputValue);
                }}
                onChange={handleOnSelectTransporter}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isListFetched ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <HeadingTableCell>Vehicle</HeadingTableCell>
              <Autocomplete
                sx={{
                  backgroundColor: "#f0f0f0",
                  width: 240,
                  borderRadius: 2,
                  // marginTop: -0.25,
                  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                    padding: "10px 8px 7.5px 15px", // same as your CSS but stable
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      height: "3rem",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2e7d32", // Hover effect
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1b5e20", // Focus effect
                    },
                  },
                }}
                options={trucksOptions.map((t) => t.vehicleNumber)}
                freeSolo
                value={truck}
                onInputChange={(event, newInputValue) => {
                  setSelectedTruck(newInputValue);
                }}
                onChange={(e, selectedOption) => {
                  if (selectedOption) {
                    const selectedTruck = trucksOptions.find(
                      (tr) => tr.vehicleNumber === selectedOption
                    );
                    setSelectedVehicle(selectedTruck);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        padding: "12px", // 👈 adjust as needed
                      },
                      // endAdornment: (
                      //   <>
                      //     {isVehicleListFetched ? (
                      //       <CircularProgress color="inherit" size={20} />
                      //     ) : null}
                      //     {params.InputProps.endAdornment}
                      //   </>
                      // ),
                    }}
                  />
                )}
              />
            </Box>
            {/* <DescriptionBox>
              <Box>
                <HeadingTableCell>Transporter</HeadingTableCell>
                <StyledSelectInput
                  name=""
                  onChange={(e) => setSelectedTransporter(e.target.value)}
                  items={["Akash Dev Transporter", "Satyam"]}
                  value={editTripProps.transporterName}
                />
              </Box>
            </DescriptionBox> */}
            {/* <DescriptionBox>
              <Box>
                <HeadingTableCell>Truck/Vehicle</HeadingTableCell>
                <StyledSelectInput
                  name="slot"
                  onChange={(e) => setSelectedTruck(e.target.value)}
                  items={timeSlots}
                  value={editTripProps.vehicleNumber}
                />
              </Box>
            </DescriptionBox> */}
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" mt={4}>
          <Button
            variant="contained"
            color="success"
            onClick={handleEditTrip}
            disabled={disableSaveButton()}
          >
            Submit
          </Button>
        </Box>
      </DialogContent>

      {isDriverAssignedError && (
        <Typography
          sx={{ textAlign: "center", color: "red", fontSize: "14px", pb: 2 }}
        >
          No driver is assigned to the selected vehicle! Please get in touch
          with the new transporter before moving forward.
        </Typography>
      )}
    </Dialog>
  );
};

export default EditTrip;

const HeadingTableCell = styled(TableCell)({
  fontWeight: "bold",
  fontSize: "13px",
  border: "none",
  height: "50px",
  textAlign: "start",
});

const DescriptionTableCell = styled(TableCell)({
  fontSize: "13px",
  border: "none",
  height: "50px",
});

const DescriptionBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  width: "200px",
  gap: 1,
});

const InputField = styled(TextField)({
  width: "150px",
  padding: "0px 4px",
  alignSelf: "center",
  justifyContent: "center",
  height: "50px",
});
